import React, { useState } from 'react';
import { RsvpForm } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';

const META: TemplateMetadata = {
  id: 'tpl_baby_shower_royal_prince_002',
  name: 'Royal Blue Baby Prince',
  occasionType: 'BABY_SHOWER',
  description: 'A sophisticated royal blue baby shower theme with hanging floral vines and a classic baby swing/cradle illustration.',
  isPremium: true,
  tags: ['baby-shower', 'premium', 'royal-blue', 'sophisticated', 'cradle'],
  colorScheme: { primary: '#1d4ed8', accent: '#3b82f6', bg: '#f0f9ff' },
};

export function RoyalBluePrince({ data }: { data: InvitationData }) {
  const baby = data.customData?.baby ?? {};
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const [showRsvpModal, setShowRsvpModal] = useState(false);

  // Read dynamic variables with default fallbacks
  const motherName = baby.motherName || 'Victoria Sterling';
  const hostedBy = baby.hostedBy || data.hostName || 'The Sterling Family';
  
  // Format Date and Day
  const rawDate = baby.eventDate || data.eventDate || new Date().toISOString();
  let formattedDate = 'November 14, 2026';
  let dayOfWeek = 'Saturday';
  try {
    const d = new Date(rawDate);
    formattedDate = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' });
  } catch (e) {}

  const eventTime = baby.eventTime || data.eventTime || '03:00 PM';
  const venueName = baby.venueName || data.eventVenue?.split(',')[0] || 'The Royal Crest Hall';
  const venueAddress = baby.venueAddress || data.eventVenue || '789 Windsor Lane, London, UK';
  
  const rsvpName = baby.rsvpName || 'Elizabeth';
  const rsvpPhone = baby.rsvpPhone || '+44 20 7946 0958';
  const message = baby.message || data.customMessage || 'We are delighted to invite you to celebrate the upcoming arrival of our little prince!';
  const dressCode = baby.dressCode || '';

  return (
    <div className="min-h-screen bg-[#f7f9fc] py-12 px-4 flex flex-col items-center justify-start font-sans relative overflow-hidden">
      {/* Import Google Fonts */}
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300..700;1,300..700&family=Parisienne&family=Inter:wght@300;400;500;600&display=swap');
        .font-serif-royal { font-family: 'Cormorant Garamond', serif; }
        .font-cursive-royal { font-family: 'Parisienne', cursive; }
        .font-sans-royal { font-family: 'Inter', sans-serif; }
        .silver-text-gradient {
          background: linear-gradient(135deg, #7f8c8d 0%, #bdc3c7 50%, #7f8c8d 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .royal-blue-border {
          border-color: #1e3a8a;
        }
      `}} />

      {/* Decorative hanging floral vines (SVG) */}
      <div className="absolute top-0 inset-x-0 h-40 opacity-20 pointer-events-none flex justify-between px-8">
        <svg className="w-32 h-full" viewBox="0 0 100 150" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10,0 Q20,40 5,80 Q-5,110 15,130" stroke="#1e3a8a" strokeWidth="2" strokeLinecap="round" />
          <circle cx="10" cy="30" r="4" fill="#3b82f6" />
          <circle cx="18" cy="50" r="5" fill="#1d4ed8" />
          <circle cx="6" cy="70" r="4" fill="#1e3a8a" />
          <circle cx="12" cy="100" r="6" fill="#60a5fa" />
          <circle cx="15" cy="130" r="3" fill="#1d4ed8" />
        </svg>
        <svg className="w-32 h-full transform scale-x-[-1]" viewBox="0 0 100 150" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10,0 Q20,40 5,80 Q-5,110 15,130" stroke="#1e3a8a" strokeWidth="2" strokeLinecap="round" />
          <circle cx="10" cy="30" r="4" fill="#3b82f6" />
          <circle cx="18" cy="50" r="5" fill="#1d4ed8" />
          <circle cx="6" cy="70" r="4" fill="#1e3a8a" />
          <circle cx="12" cy="100" r="6" fill="#60a5fa" />
        </svg>
      </div>

      {/* Main Card */}
      <div className="w-full max-w-[480px] aspect-[9/16] bg-white rounded-[24px] shadow-2xl p-8 relative flex flex-col justify-between border-[12px] border-[#e0f2fe] overflow-hidden">
        
        {/* Silver Inner Border */}
        <div className="absolute inset-2 border border-[#cbd5e1] rounded-[16px] pointer-events-none"></div>
        <div className="absolute inset-3 border border-[#94a3b8]/20 rounded-[14px] pointer-events-none"></div>

        {/* Content Container */}
        <div className="flex-grow flex flex-col justify-center items-center text-center relative z-10 py-2 px-2">
          
          {/* Baby Swing / Cradle SVG Illustration */}
          <div className="w-20 h-20 mb-4 opacity-90">
            <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              {/* Stand ropes */}
              <line x1="20" y1="10" x2="35" y2="60" stroke="#94a3b8" strokeWidth="1.5" />
              <line x1="80" y1="10" x2="65" y2="60" stroke="#94a3b8" strokeWidth="1.5" />
              <line x1="35" y1="60" x2="65" y2="60" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round" />
              {/* Cradle Swing body */}
              <path d="M30,55 C30,75 70,75 70,55 Z" fill="#eff6ff" stroke="#1d4ed8" strokeWidth="2" />
              <path d="M30,55 C35,62 65,62 70,55" fill="none" stroke="#60a5fa" strokeWidth="1.5" />
              {/* Crown on top of swing */}
              <path d="M45,35 L47,40 L50,35 L53,40 L55,35 L55,43 L45,43 Z" fill="#3b82f6" />
              <circle cx="50" cy="47" r="1.5" fill="#1e3a8a" />
              {/* Small stars */}
              <path d="M25,25 L26,27 L28,27 L26,28 L27,30 L25,29 L23,30 L24,28 L22,27 L24,27 Z" fill="#60a5fa" />
              <path d="M75,20 L76,22 L78,22 L76,23 L77,25 L75,24 L73,25 L74,23 L72,22 L74,22 Z" fill="#60a5fa" />
            </svg>
          </div>

          <span className="font-sans-royal text-[9px] tracking-[0.3em] text-[#1d4ed8] uppercase font-bold mb-3">
            Please Join Us In Celebrating
          </span>

          <h2 className="font-serif-royal text-3xl md:text-4xl text-[#1e3a8a] tracking-widest font-medium mb-1">
            ROYAL BABY SHOWER
          </h2>

          <span className="font-sans-royal text-xs text-[#3b82f6] italic mb-6">
            in honor of
          </span>

          <h1 className="font-cursive-royal text-5xl text-[#1e3a8a] leading-none mb-6 font-semibold py-1">
            {motherName}
          </h1>

          <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#cbd5e1] to-transparent mb-6"></div>

          <p className="font-sans-royal text-xs tracking-wide text-stone-500 mb-6 leading-relaxed">
            Hosted with delight by <br />
            <span className="font-semibold text-stone-800">{hostedBy}</span>
          </p>

          {/* Date & Time block */}
          <div className="flex flex-col items-center mb-6">
            <span className="font-serif-royal text-xl text-[#1e3a8a] font-semibold">
              {dayOfWeek}, {formattedDate}
            </span>
            <span className="font-sans-royal text-xs tracking-wider text-[#3b82f6] uppercase font-semibold mt-1">
              At {eventTime}
            </span>
          </div>

          {/* Venue block */}
          <div className="flex flex-col items-center mb-6 max-w-[280px]">
            <span className="font-serif-royal text-sm font-semibold text-stone-800 mb-1">
              {venueName}
            </span>
            <span className="font-sans-royal text-xs text-stone-500 leading-normal">
              {venueAddress}
            </span>
          </div>

          {/* Dress Code */}
          {dressCode && (
            <div className="mb-6 px-4 py-1 bg-[#eff6ff] rounded-md border border-[#dbeafe]">
              <span className="font-sans-royal text-[10px] tracking-wider text-[#1d4ed8] uppercase font-medium">
                Dress Code: {dressCode}
              </span>
            </div>
          )}

          {/* Message */}
          {message && (
            <p className="font-serif-royal text-xs text-stone-500 italic leading-relaxed max-w-[300px] mb-6">
              "{message}"
            </p>
          )}

          {/* RSVP Button inside Card */}
          <div className="mt-2 w-full flex justify-center">
            <button
              type="button"
              onClick={() => setShowRsvpModal(true)}
              className="inline-flex items-center justify-center px-8 py-3 bg-[#1e3a8a] hover:bg-[#152c6f] text-white font-bold text-sm uppercase tracking-widest rounded-full shadow-md transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0"
            >
              RSVP Now
            </button>
          </div>

        </div>
      </div>

      {/* RSVP Modal Popup */}
      {showRsvpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-stone-950/65 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setShowRsvpModal(false)}
          />
          
          {/* Modal Container */}
          <div className="relative w-full max-w-md bg-stone-900 border border-white/10 rounded-3xl p-6 shadow-2xl z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setShowRsvpModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <h3 className="text-2xl font-serif text-white font-semibold">Are You Attending?</h3>
              <p className="text-white/60 text-sm mt-1">Please RSVP to let us know if you're coming!</p>
            </div>
            
            <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
          </div>
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: RoyalBluePrince });
